import React, { useEffect, useState } from 'react';
import { Eye, Loader2, ShieldCheck, ShieldOff } from 'lucide-react';
import type { SupportAccessDTO } from '../../global';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { formatClock, formatDate } from '../../components/ui/styles';
import { t } from '../../lib/i18n';

interface SupportAccessDialogProps {
  open: boolean;
  meetingId: string;
  onClose: () => void;
}

const when = (iso: string) => `${formatDate(iso)} ${formatClock(iso)}`;

/**
 * Toplantının destek erişimi (veri koruma programı §4.2/1). Açıkken TalkNote destek
 * ekibi bu toplantının içeriğini 48 saat boyunca görebilir; kullanıcı istediği an
 * kapatır. Altta kimin, ne zaman baktığı listelenir.
 */
export const SupportAccessDialog: React.FC<SupportAccessDialogProps> = ({ open, meetingId, onClose }) => {
  const [state, setState] = useState<SupportAccessDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    window.zabit
      .getSupportAccess(meetingId)
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || t('Destek erişimi okunamadı.', 'Could not load support access.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, meetingId]);

  const toggle = async (enabled: boolean) => {
    setSaving(true);
    setError(null);
    try {
      setState(await window.zabit.setSupportAccess(meetingId, enabled));
    } catch (err) {
      setError((err as Error).message || t('Değiştirilemedi, tekrar dene.', 'Could not change it, try again.'));
    } finally {
      setSaving(false);
    }
  };

  const hours = state?.hours ?? 48;
  const active = state?.active ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Destek erişimi', 'Support access')}
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('Kapat', 'Close')}
          </Button>
          {active ? (
            <Button onClick={() => void toggle(false)} disabled={saving || loading} className="bg-rose-600 text-white hover:bg-rose-500">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <ShieldOff className="size-4" />}
              {t('Erişimi kapat', 'Turn off access')}
            </Button>
          ) : (
            <Button onClick={() => void toggle(true)} disabled={saving || loading}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
              {t(`${hours} saatliğine aç`, `Allow for ${hours} hours`)}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4 text-sm leading-relaxed">
        <p className="text-muted-foreground">
          {t(
            `Ekibimiz toplantılarının içeriğini göremez. Bir sorunu incelememizi istersen bu toplantı için erişimi açabilirsin: destek ekibi yalnızca bu toplantının başlığını, özetini, transkriptini, notlarını ve katılımcılarını ${hours} saat boyunca görebilir. Ses kaydı dinlenmez. Erişimi istediğin an kapatabilirsin; her görüntüleme aşağıda listelenir.`,
            `Our team can’t see the content of your meetings. If you’d like us to look into a problem, you can allow access to this meeting: support can see only this meeting’s title, summary, transcript, notes and participants for ${hours} hours. The recording isn’t played. You can turn access off at any time; every view is listed below.`
          )}
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('Yükleniyor…', 'Loading…')}
          </div>
        ) : active ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5 text-emerald-600 dark:text-emerald-300">
            {t(`Açık · ${when(active.expiresAt)} tarihinde kendiliğinden kapanır.`, `On · turns off automatically on ${when(active.expiresAt)}.`)}
          </div>
        ) : state ? (
          <div className="rounded-xl border border-border px-3.5 py-2.5 text-muted-foreground">
            {t('Kapalı · destek ekibi bu toplantının içeriğini göremez.', 'Off · support can’t see this meeting’s content.')}
          </div>
        ) : null}

        {error && <p className="text-rose-500">{error}</p>}

        {state && (
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('Kim baktı', 'Who looked')}
            </div>
            {state.views.length === 0 ? (
              <p className="text-muted-foreground">{t('Bu toplantıya destek ekibinden kimse bakmadı.', 'No one from support has viewed this meeting.')}</p>
            ) : (
              <ul className="space-y-1">
                {state.views.map((view, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Eye className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="break-all">{view.by}</span>
                    <span className="ml-auto shrink-0 text-muted-foreground">{when(view.at)}</span>
                  </li>
                ))}
              </ul>
            )}
            {state.grants.length > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                {t(
                  `Bu toplantı için ${state.grants.length} kez erişim açıldı; ilki ${when(state.grants[state.grants.length - 1].grantedAt)}.`,
                  `Access was allowed ${state.grants.length} time(s) for this meeting; first on ${when(state.grants[state.grants.length - 1].grantedAt)}.`
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
